import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import { useAppSelector } from "Components/Hooks/hooks";
import Icon from "Components/Icons/Icon";
import ManageSectionModal from "Components/Quotes/ManageQuotes/ManageSectionModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Col } from "reactstrap";
import { createSelector } from "reselect";
import {
  selectSectionsState,
  updateSectionLocally,
} from "slices/section/reducer";
import {
  addSectionsToQuote,
  getSections,
  updateQuoteStatus,
} from "slices/thunks";
import { QuoteDetailsPayload } from "types/quote.api";
import { SectionPayloadData } from "types/section.api";
import { ErrorToast } from "utils/toast";
import parse from "html-react-parser";

interface props {
  quoteDetails: QuoteDetailsPayload | null;
  onSubmit: () => void;
  isEdit: boolean;
}

const SectionsPane = ({ quoteDetails, onSubmit, isEdit }: props) => {
  const [showManageSectionModal, setShowManageSectionModal] = useState(false);
  const [isEditSectionModal, setIsEditSectionModal] = useState<boolean>(false);
  const dispatch: any = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSection, setSelectedSection] =
    useState<SectionPayloadData | null>(null);
  const addItems = useAppSelector((state) => state.item.addItems);

  const isQuoteEditLoading = useAppSelector(
    (s) => s.quote.status.updateQuoteStatus,
  );
  const isSectionEditLoading = useAppSelector(
    (s) => s.section.status.addToQuote,
  );

  const sectionSelector = useMemo(
    () =>
      createSelector(selectSectionsState, (state) => ({
        sections: state.sections,
        isLoading: state.status.fetch,
        meta: state.meta,
      })),
    [],
  );

  const { sections, meta } = useAppSelector(sectionSelector);

  // Handle checkbox toggle for individual section
  const handleSectionCheckboxToggle = useCallback(
    (sectionId: number, currentis_added: boolean) => {
      dispatch(
        updateSectionLocally({
          sectionId,
          updates: { is_added: !currentis_added },
        }),
      );
    },
    [dispatch],
  );

  // Handle select all checkbox
  const handleSelectAll = useCallback(() => {
    const allChecked = sections.every((section) => section.is_added);
    sections.forEach((section) => {
      dispatch(
        updateSectionLocally({
          sectionId: section.id,
          updates: { is_added: !allChecked },
        }),
      );
    });
  }, [dispatch, sections]);

  // Get checked sections
  const getCheckedSections = (): SectionPayloadData[] => {
    return sections.filter((section) => section.is_added === true);
  };

  // Handle finalize quote
  const handleFinalizeQuote = async () => {
    const checkedSections = getCheckedSections();
    console.log("Checked Sections:", checkedSections);

    // TODO: Implement your finalize quote logic here
    // For example:
    if (quoteDetails?.id) {
      if (addItems.length === 0) {
        ErrorToast("Please select item to proceed further.");
        return;
      }
      try {
        if (quoteDetails.status === "draft") {
          await dispatch(
            updateQuoteStatus({
              status: "completed",
              quote_id: quoteDetails.id,
            }),
          )?.unwrap();
        }
        await dispatch(
          addSectionsToQuote({
            quote_id: quoteDetails?.id,
            sections: checkedSections.map(({ title, content, sort }) => ({
              title,
              content,
              sort,
            })),
          }),
        )?.unwrap();
        onSubmit();
      } catch (error) {
        console.log("Error finalizing quote:", error);
      }
    }
  };

  function toggleManageSectionModal() {
    setIsEditSectionModal(false);
    setShowManageSectionModal(!showManageSectionModal);
  }

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1);
  };

  const handleEditSection = useCallback((section: SectionPayloadData) => {
    setSelectedSection(section);
    setIsEditSectionModal(true);
    setShowManageSectionModal(true);
  }, []);

  const sectionColumn = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            className="form-check-input"
            id="checkBoxAll"
            checked={sections.length > 0 && sections.every((s) => s.is_added)}
            onChange={handleSelectAll}
          />
        ),
        cell: (cell: any) => {
          const section = cell.row.original as SectionPayloadData;
          return (
            <input
              type="checkbox"
              className="contactCheckBox form-check-input"
              checked={section.is_added || false}
              onChange={() =>
                handleSectionCheckboxToggle(
                  section.id,
                  section.is_added || false,
                )
              }
            />
          );
        },
        id: "#",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "ORDER",
        accessorKey: "sort",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue() ? cell.getValue() : "-"}
            </span>
          );
        },
      },
      {
        header: "SECTION",
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue() ? cell.getValue() : "-"}
            </span>
          );
        },
      },
      {
        header: "DESCRIPTION",
        accessorKey: "content",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="html-content">{parse(cell.getValue()) ?? "-"}</div>
        ),
      },
      {
        header: "ACTIONS",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="d-flex gap-3">
            <div
              className="cursor-pointer"
              onClick={() => handleEditSection(cell.row.original)}
            >
              <Icon name="Edit" />
            </div>
          </div>
        ),
      },
    ],
    [sections, handleSelectAll, handleSectionCheckboxToggle, handleEditSection],
  );

  useEffect(() => {
    console.log("QUOTE ADD SECTION", quoteDetails?.id);

    if (quoteDetails?.id) {
      if (isEdit) {
        dispatch(
          getSections({
            page: currentPage,
            search: searchTerm,
            quote_id: quoteDetails?.id,
          }),
        );
      } else {
        dispatch(
          getSections({
            page: currentPage,
            search: searchTerm,
          }),
        );
      }
    } else {
      //REMOVE
      // dispatch(
      //   getSections({
      //     page: currentPage,
      //     search: searchTerm,
      //     quote_id: 965,
      //   })
      // );
    }
  }, [dispatch, currentPage, searchTerm, quoteDetails, isEdit]);
  return (
    <>
      <TableContainer
        tableId="quoteSectionTab"
        columns={sectionColumn}
        data={sections}
        isGlobalFilter={true}
        customPageSize={sections.length}
        divClass="table-responsive table-card mb-2 bg-white"
        tableClass="align-middle table-wrap table-borderless"
        thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
        theadClass="table-light"
        SearchPlaceholder="Search section"
        searchAlign="left"
        showPagination={false}
        rightContent={
          <CustomButton
            text="New Section"
            leftIcon={<i className="ri-add-line me-1 align-bottom fs-5" />}
            className="bg-primary br-7"
            onClick={() => {
              setShowManageSectionModal(true);
            }}
          />
        }
        currentPage={meta?.current_page}
        totalPages={meta?.last_page || 0}
        onPageChange={(page: number) => setCurrentPage(page)}
        fromPage={meta?.from}
        toPage={meta?.to}
        totalItems={meta?.total}
        onSearch={handleSearch}
      />
      <Col lg={12}>
        <CustomButton
          text="Finalise Quote"
          className="bg-primary br-7 mt-4 "
          onClick={handleFinalizeQuote}
          loading={
            isQuoteEditLoading === "loading" ||
            isSectionEditLoading === "loading"
          }
        />
      </Col>
      <ManageSectionModal
        isEdit={isEditSectionModal}
        showModal={showManageSectionModal}
        toggle={toggleManageSectionModal}
        existingSectionData={selectedSection}
        isFromQuote={true}
      />
    </>
  );
};

export default SectionsPane;
